import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Loader2,
  Search,
  Handshake,
  ShieldCheck,
  Truck,
  Info,
  Car,
  X,
} from "lucide-react";
import LegalLayout from "../components/LegalLayout";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";

/**
 * Public "Privati" page — for private (non-dealer) customers.
 *
 * NOT self-service: no account is created, no login. Lead capture only.
 * Submissions go to POST /api/public/private-leads and land in db.requests
 * with type="private_lead" so the AUTEXIO team can call the lead back.
 *
 * Wording is deliberately prudent: no "garantito", no "escrow".
 * Protected payment is labelled "in arrivo — partner autorizzato".
 */
export default function PrivatiPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCar = (searchParams.get("car") || "").slice(0, 300);
  const initialVid = (searchParams.get("vid") || "").slice(0, 48);
  const [selectedVid, setSelectedVid] = useState(initialVid || null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    car: initialCar,
    budget: "",
    note: "",
    honeypot: "", // hidden anti-bot field
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  // If we arrived with a vid but no car label (e.g. shortened URL), fetch the
  // whitelisted showcase detail and hydrate the "car" field with the sanitized name.
  useEffect(() => {
    if (!initialVid || initialCar) return;
    (async () => {
      try {
        const { data } = await api.get(`/public/showcase-vehicles/${initialVid}`);
        const label = [data.brand, data.model, data.trim, data.year].filter(Boolean).join(" ");
        if (label) setForm((s) => ({ ...s, car: label }));
      } catch {
        /* silent — user just fills the field themselves */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSelectedCar = () => {
    setSelectedVid(null);
    setForm((s) => ({ ...s, car: "" }));
    // clean URL params so a page reload doesn't re-hydrate
    const p = new URLSearchParams(searchParams);
    p.delete("car");
    p.delete("vid");
    setSearchParams(p, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    // client-side "at least one contact" check
    if (!form.email.trim() && !form.whatsapp.trim()) {
      setErr(t("privati.form_error_contact_required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/private-leads", {
        name: form.name.trim(),
        email: form.email.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        car: form.car.trim(),
        vehicle_id: selectedVid || null,
        budget: form.budget.trim() || null,
        note: form.note.trim() || null,
        lang: i18n.language || null,
        honeypot: form.honeypot || null,
      });
      setSent(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout
      testid="privati-page"
      overline={t("privati.overline")}
      title={t("privati.title")}
    >
      <p
        data-testid="privati-hero-message"
        className="text-slate-600 -mt-4 mb-6 max-w-2xl leading-relaxed"
      >
        {t("privati.hero_message")}
      </p>

      <div className="mb-10 flex flex-wrap gap-3" data-testid="privati-hero-ctas">
        <Button asChild className="h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 shadow-sm">
          <Link to="/privati/auto" data-testid="privati-browse-cars-btn">
            <Car className="w-4 h-4 mr-2" />
            {t("privati.browse_cars")}
          </Link>
        </Button>
        <Button asChild variant="outline" className="h-11 rounded-full border-slate-200 px-6 font-semibold">
          <a href="#privati-form" data-testid="privati-request-generic-btn">
            {t("privati.request_generic")}
          </a>
        </Button>
      </div>

      {/* Four key points */}
      <div
        data-testid="privati-points"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
      >
        <PointCard
          testid="privati-point-1"
          Icon={Search}
          title={t("privati.point1_title")}
          body={t("privati.point1_body")}
        />
        <PointCard
          testid="privati-point-2"
          Icon={Handshake}
          title={t("privati.point2_title")}
          body={t("privati.point2_body")}
        />
        <PointCard
          testid="privati-point-3"
          Icon={ShieldCheck}
          title={t("privati.point3_title")}
          body={t("privati.point3_body")}
          footnote={t("privati.point3_footnote")}
        />
        <PointCard
          testid="privati-point-4"
          Icon={Truck}
          title={t("privati.point4_title")}
          body={t("privati.point4_body")}
        />
      </div>

      <div id="privati-form" className="grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Form (3/5) */}
        <div className="md:col-span-3">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 md:p-7 shadow-sm">
            <p className="text-[11px] uppercase tracking-[0.24em] text-blue-600 font-semibold">
              {t("privati.form_overline")}
            </p>
            <h2 className="mt-1 font-heading font-bold text-2xl md:text-3xl tracking-tight text-slate-950">
              {t("privati.form_title")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">{t("privati.form_subtitle")}</p>

            {selectedVid && form.car && !sent && (
              <div
                data-testid="privati-selected-car-chip"
                className="mt-5 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3"
              >
                <Car className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
                    {t("privati.selected_car_label")}
                  </p>
                  <p className="text-[13px] font-semibold text-slate-950 truncate">{form.car}</p>
                </div>
                <button
                  type="button"
                  onClick={clearSelectedCar}
                  data-testid="privati-selected-car-clear"
                  aria-label={t("privati.selected_car_clear")}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {sent ? (
              <div
                data-testid="privati-form-success"
                className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center"
              >
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-lg text-slate-950">
                  {t("privati.success_title")}
                </h3>
                <p className="mt-2 text-sm text-emerald-900/80">
                  {t("privati.success_body")}
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="mt-6 space-y-4"
                data-testid="privati-form"
              >
                {/* honeypot — hidden from real users */}
                <div className="hidden" aria-hidden>
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.honeypot}
                      onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
                    />
                  </label>
                </div>

                <div>
                  <Label htmlFor="p-name" className="text-xs font-medium text-slate-600">
                    {t("privati.field_name")}
                  </Label>
                  <Input
                    id="p-name"
                    required
                    data-testid="privati-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="p-email" className="text-xs font-medium text-slate-600">
                      {t("privati.field_email_optional")}
                    </Label>
                    <Input
                      id="p-email"
                      type="email"
                      data-testid="privati-email-input"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 h-11 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="p-whatsapp" className="text-xs font-medium text-slate-600">
                      {t("privati.field_whatsapp_optional")}
                    </Label>
                    <Input
                      id="p-whatsapp"
                      type="tel"
                      inputMode="tel"
                      data-testid="privati-whatsapp-input"
                      value={form.whatsapp}
                      onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                      className="mt-1 h-11 rounded-xl"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 -mt-2">
                  {t("privati.contact_hint")}
                </p>

                <div>
                  <Label htmlFor="p-car" className="text-xs font-medium text-slate-600">
                    {t("privati.field_car")}
                  </Label>
                  <Input
                    id="p-car"
                    required
                    data-testid="privati-car-input"
                    value={form.car}
                    onChange={(e) => setForm({ ...form, car: e.target.value })}
                    placeholder={t("privati.field_car_placeholder")}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>

                <div>
                  <Label htmlFor="p-budget" className="text-xs font-medium text-slate-600">
                    {t("privati.field_budget_optional")}
                  </Label>
                  <Input
                    id="p-budget"
                    data-testid="privati-budget-input"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    placeholder={t("privati.field_budget_placeholder")}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>

                <div>
                  <Label htmlFor="p-note" className="text-xs font-medium text-slate-600">
                    {t("privati.field_note_optional")}
                  </Label>
                  <Textarea
                    id="p-note"
                    rows={4}
                    data-testid="privati-note-input"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>

                {err && (
                  <div
                    data-testid="privati-error"
                    className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                  >
                    {err}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="privati-submit"
                  className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {t("privati.sending")}
                    </>
                  ) : (
                    t("privati.submit")
                  )}
                </Button>

                <p
                  data-testid="privati-form-note"
                  className="text-[12px] leading-snug text-slate-500 flex items-start gap-2"
                >
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                  <span>{t("privati.form_note")}</span>
                </p>
              </form>
            )}
          </div>
        </div>

        {/* How it works (2/5) */}
        <aside className="md:col-span-2 space-y-4">
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-6">
            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
              {t("privati.how_overline")}
            </p>
            <ol className="mt-4 space-y-4">
              <HowStep n={1} title={t("privati.how_1_title")} body={t("privati.how_1_body")} />
              <HowStep n={2} title={t("privati.how_2_title")} body={t("privati.how_2_body")} />
              <HowStep n={3} title={t("privati.how_3_title")} body={t("privati.how_3_body")} />
              <HowStep n={4} title={t("privati.how_4_title")} body={t("privati.how_4_body")} />
            </ol>
          </div>

          <div
            data-testid="privati-disclaimer"
            className="rounded-2xl border border-blue-100 bg-blue-50/60 p-5"
          >
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-700">
              <ShieldCheck className="w-3.5 h-3.5" />
              {t("privati.disclaimer_overline")}
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
              {t("privati.disclaimer_body")}
            </p>
          </div>
        </aside>
      </div>
    </LegalLayout>
  );
}

function PointCard({ Icon, title, body, footnote, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm"
    >
      <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
        <Icon className="w-4 h-4 text-blue-600" />
      </div>
      <h3 className="mt-4 font-heading font-semibold text-[15px] tracking-tight text-slate-950">
        {title}
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{body}</p>
      {footnote && (
        <p className="mt-2 text-[11px] italic leading-snug text-slate-400">{footnote}</p>
      )}
    </div>
  );
}

function HowStep({ n, title, body }) {
  return (
    <li className="flex gap-3">
      <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
        {n}
      </div>
      <div>
        <div className="text-[13px] font-semibold text-slate-950">{title}</div>
        <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">{body}</div>
      </div>
    </li>
  );
}
